/* The seam. Every screen talks to this typed client and nothing else.
   Today the network layer is intercepted by MSW (src/mocks); connecting
   the real Worker later means deleting handlers, not touching screens. */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

/* ---------- domain types (schema v3 vocabulary) ---------- */
/* The full §15 ladder (sourced: WF-05 CA-01 annotation): Draft → Test →
   Scheduled → Active → Paused → Ended → Archived. Ended keeps issued terms. */
export type CampaignStatus = 'draft' | 'test' | 'scheduled' | 'active' | 'paused' | 'ended' | 'archived'
/* D-83 (07/10) · the customer's order, per campaign (contract 05 §5.5): the gift right after the
   wheel, or after the demande (the sealed order). `pendant` is exactly the sealed flow.
   D-87 (07/10) · a third order: the demande at the welcome, then the wheel. `demande_avant_roue` is the
   working name (builder-step-moment-v2) until Technical names the API value. */
export type Flow = 'cadeau_direct' | 'cadeau_apres_invitation' | 'demande_avant_roue'
export interface Campaign {
  id: string; name: string; location: string; status: CampaignStatus
  meta: string; testedAt?: string
  roue?: string // ruling 04/09 · the roue this campaign allocated (frozen per version, 0011)
  flow?: Flow   // D-83 · absent = derived from the moment by the API (flowOf)
  /** I-1 (08/10, 0032) · the establishments whose « campagne principale » this is (contract 05 §5.4); absent in the mock fixtures */
  principal?: string[]
  /** 0035 P7 (lib-pack-20) · the row's « par visite » (contract 05 §5.4): a live Parcours where a visit plays its own gift or order; absent in the fixtures */
  perVisit?: boolean
  /** 0036 (contract 05 §5.4) · the row's « Étiquettes », tag ids; absent in the fixtures and until lib-pack-21 */
  tags?: string[]
}
/** One thing to do. `to` is the screen that fixes it; without it Accueil routes by the words (batch 2). */
export interface ActionItem { id: string; title: string; meta: string; action: string; to?: string }
export interface SuccessMoment { id: string; title: string; body: string; action: string }
export interface Overview {
  workspace: string
  returningThisMonth: number; knownCustomers: number; giftsUsedValue: string
  participations30d: number; giftsRedeemed30d: number
  /** « Invitations ouvertes · 30 j »: an invitation opened, never a review counted (reporting spec 08/10).
      The fixtures use Résultats' funnel ratio, so both screens tell one story per world. */
  invitationsOpened30d: number
  googleRating: { from: string; to: string } | null
  aTraiter: ActionItem[]; successMoment: SuccessMoment | null
  checklist: { label: string; done: boolean }[] | null
}
export interface GiftSummary {
  encours: string; activeGifts: number; expiring30d: string
  unclaimedCount: number; oneAtATime: boolean
  /** €/cadeau attribué — the canonical constant RJ-07's euro line reads.
      Ratified 31/08: 7,90 € in cruising so the drawn 95 € reproduces;
      data.ts gift types are a partial list, reconciled at Step 8. */
  avgGiftCost: number
  types: { id: string; name: string; cost: string; stock: number | null; status: 'ok' | 'low' | 'out' }[]
}
/* GET /v1/app/clients (contract 10 §10.4) — codes, never sentences: the Clients screen
   writes the French. `thresholds` ride along so no screen hard-codes a cohort figure
   (D-85: habitué = 3 visits in 180 days, perdu de vue = 90 days; a workspace setting later). */
export type ClientStage = 'perdu_de_vue' | 'habitue' | 'actif' | 'nouveau'
export interface ClientRow {
  id: string; firstName: string | null
  /** owner/admin only — a manager's rows never carry it */
  email?: string
  /** owner/admin only, beside `email` (Technical, 0025): false while the address is still unconfirmed —
      D-89 « link confirms »: the gift e-mail's link confirms it on first tap, so false is the normal case at first */
  emailConfirmed?: boolean
  visits: number; stage: ClientStage; giftsHeld: number; giftsExpired: number
  lastVisitAt: string
  /** the row's dot: a gift to use · nothing pending · not seen for a while */
  state: 'ok' | 'done' | 'warn'
}
export interface ClientsCohorts {
  cohorts: { returningThisMonth: number; regulars: number; unusedGift: number; lapsed: number; known: number }
  thresholds: { regularsVisits: number; regularsDays: number; lapsedDays: number }
  items: ClientRow[]
  nextCursor: string | null
}
/* Displays are registered entities (§J — owner direction 31/08, NOW-constraints):
   a Support IS a Display; its QR/NFC target is the PERMANENT token URL
   go.buildmyreviews.app/d/{token}, resolved server-side — never a hardcoded
   destination, so a printed QR never reprints. The model carries all four
   access methods today; kiosk/web fixtures arrive with the Displays module. */
export type AccessMethod = 'qr' | 'nfc' | 'kiosk' | 'web'
/** The host of the API this build names (VITE_API_URL), when it names one. */
const apiHost = (() => { try { return import.meta.env.VITE_API_URL ? new URL(import.meta.env.VITE_API_URL).hostname : null } catch { return null } })()
/** The player host for this environment: staging-go. from a staging host, go. otherwise. The host that decides is the
    API's when the build names one, else this page's (the door row: a staging build may sit on a Pages host that does not
    say « staging », while its VITE_API_URL does, staging-api.…). No new environment variable. */
export function playerOrigin(host = apiHost ?? (typeof location === 'undefined' ? '' : location.hostname)): string {
  return /(^|\.)staging[-.]/.test(host) || host.startsWith('staging') ? 'https://staging-go.buildmyreviews.app' : 'https://go.buildmyreviews.app'
}
/** QR-04 · the one place the customer URL of a support is spelled — what its QR, NFC tag, link and
    e-mail signature carry. Every screen that shows or copies it reads this. */
export const supportUrl = (token: string, host?: string) => `${playerOrigin(host)}/d/${encodeURIComponent(token)}`
export interface Support {
  id: string; name: string; kind: string; scans7d: number
  lastScan: string | null; silentDays: number | null
  /** permanent BMR token — the display’s identity across reprints & campaigns */
  token: string
  methods: AccessMethod[]
  /** D-32 (05/09): a support belongs to exactly one establishment */
  locationId?: string
  /** D-38 (05/09): « Épinglé » to one campaign, or null = « Automatique » (served by priority at each scan) */
  pinned?: string | null
  /* read from the API (contract 06 §6.1, `supportOfApp`): what the fixtures leave out */
  place?: string | null; memberId?: string; design?: Record<string, unknown>
  /** the API's own figures (`numbers`), in the app's words; the fixtures derive theirs from scans7d */
  numbers?: { scans30: number; participations: number; lastScan: string | null }
  /** 0036 (contract 06 §6.1) · the support's « Étiquettes », tag ids; absent in the fixtures and until lib-pack-21 */
  tags?: string[]
  /** lib-pack-22 · 0038 (06 §6.1) · the printed record, null until « Imprimé »; absent in the fixtures */
  printed?: { at: string; stale: boolean } | null
  /** lib-pack-23 · 0039 (06 §6.1) · its « Emplacement » (lib/organisation › supportPlace reads it); absent in the fixtures */
  placeId?: string | null
}
/** Why a support opens what it opens (the API's `resolveSupport`, contract 06 §6.3; the app's `servedBy`). Serves
    nothing: fixed_paused, fixed_scheduled, fixed_not_live, principal_paused. */
export type ServedReason = 'fixed' | 'fixed_not_live' | 'fixed_paused' | 'fixed_scheduled' | 'principal' | 'principal_paused' | 'active_first' | 'none'
/** A support as the API answers it (contract 06 §6.1). `pointer` is exactly the app's Pointer; `serving` is the
    resolver's answer now, its sentence the app's. */
export interface AppSupport {
  id: string; name: string; kind: string; genre: string | null; locationId: string; place: string | null
  token: string; url: string; methods: AccessMethod[]
  pointer: { mode: 'active' } | { mode: 'fixed'; campaignId: string }
  memberId: string | null; active: boolean
  serving: { campaignId: string | null; name: string | null; reason: ServedReason }
  design: Record<string, unknown> | null
  numbers: { scans30d: number; participations30d: number; lastScanAt: string | null; silentDays: number | null }
  createdAt: string | null; placedAt: string | null
  /** 0036 (lib-pack-21) · the support's « Étiquettes », tag ids in the list's order */
  tags?: string[]
  /** 0038 (lib-pack-22) · null until POST …/printed; then when, and whether what it serves changed since (06 §6.1) */
  printed?: { at: string; stale: boolean } | null
  /** 0039 (lib-pack-23) · its « Emplacement », null when none; `place` is then that place's name (read-only). Absent
      before 0039, when `place` is the free text. */
  placeId?: string | null
}
/** The word a row says after the name (« Comptoir A6 · chevalet »), from the API's Scope v5 « Genre ». */
export const GENRE_WORD: Record<string, string> = {
  'chevalet-a6': 'chevalet', 'sticker-50': 'sticker', 'affiche-a4': 'affiche', 'affiche-a3': 'grand format',
  'vitrine-150': 'grand format', 'ecran-portrait': 'écran', 'ecran-paysage': 'écran', nfc: 'NFC', widget: 'widget', lien: 'lien',
  signature: 'signature', personnel: 'personnel',
}
/** The other way: the « Genre » a new support is created with — its designed model, else the first genre its word names. */
export function genreOf(kind: string, model?: string | null): string {
  if (model && model in GENRE_WORD) return model
  return Object.keys(GENRE_WORD).find(g => GENRE_WORD[g] === kind) ?? kind
}
/** « à l’instant », « il y a 2 h », « hier », « il y a 3 j »: a scan time as the fixtures say it. */
export function agoFr(iso: string | null, now = Date.now()): string | null {
  const t = iso ? Date.parse(iso) : NaN
  if (Number.isNaN(t)) return null
  const min = Math.max(0, Math.round((now - t) / 60000))
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  if (min < 24 * 60) return `il y a ${Math.round(min / 60)} h`
  const days = Math.round(min / (24 * 60))
  return days === 1 ? 'hier' : `il y a ${days} j`
}
/** The API's support → the row the screens read. The pointer is the server's, the figures its own. */
export function supportOfApp(s: AppSupport): Support {
  const lastScan = agoFr(s.numbers.lastScanAt)
  return {
    id: s.id, name: s.name, kind: (s.genre && GENRE_WORD[s.genre]) ?? s.genre ?? s.kind, token: s.token, methods: s.methods,
    locationId: s.locationId, pinned: s.pointer.mode === 'fixed' ? s.pointer.campaignId : null,
    scans7d: Math.round(s.numbers.scans30d * 7 / 30), lastScan, silentDays: s.numbers.silentDays,
    place: s.place, ...(s.memberId ? { memberId: s.memberId } : {}), /* lib-pack-17 · the API's `null` reads as it is */
    ...(s.design && typeof s.design.model === 'string' ? { design: s.design } : {}),
    ...(s.genre ? { genre: s.genre } : {}),
    numbers: { scans30: s.numbers.scans30d, participations: s.numbers.participations30d, lastScan },
    ...(s.tags ? { tags: s.tags } : {}), /* lib-pack-21 · the row's étiquettes */
    ...(s.printed !== undefined ? { printed: s.printed } : {}), /* lib-pack-22 · the printed record (0038) */
    ...(s.placeId !== undefined ? { placeId: s.placeId } : {}), /* lib-pack-23 · its emplacement (0039) */
  }
}
/* Every participation-side event carries its display + access method (§J):
   per-display and per-method analytics are only possible if attribution is
   captured at the event, never inferred later. displayId is null only for
   events with no entry surface (e.g. a merchant-side action). */
export interface EventRow {
  id: string; at: string; kind: string; label: string
  displayId: string | null
  accessMethod: AccessMethod | null
}
export interface Location {
  id: string; name: string; status: 'active' | 'pending' | 'inactive'; rating: string | null
}
/** GET /v1/app/gifts/summary (contract 03 §3.3): cents, never sentences. */
export interface AppGiftSummary {
  outstandingCents: number; activeGifts: number; expiring30dCents: number; unclaimedCount: number; oneAtATime: boolean
  avgGiftCostCents: number | null
  types: { id: string; name: string; costCents: number; stock: number | null; status: 'ok' | 'low' | 'out' }[]
}
const euros = (cents: number) => `${Math.round(cents / 100).toLocaleString('fr-FR')} €`
const eurosCents = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
/** The server's summary, said as the screens say it (« 1 284 € », « 2,50 € »). No cost yet → 0, as the day-one fixture. */
export const giftSummaryOfApp = (g: AppGiftSummary): GiftSummary => ({
  encours: euros(g.outstandingCents), activeGifts: g.activeGifts, expiring30d: euros(g.expiring30dCents), unclaimedCount: g.unclaimedCount,
  oneAtATime: g.oneAtATime, avgGiftCost: g.avgGiftCostCents == null ? 0 : g.avgGiftCostCents / 100,
  types: g.types.map(t => ({ id: t.id, name: t.name, cost: eurosCents(t.costCents), stock: t.stock, status: t.status })),
})
export interface Insight { id: string; title: string; meta: string; action: string }

/* ---------- transport (rows 1.4–1.6) ----------
   The ONE fetcher every seam file shares — route helpers import { get, call }
   from here instead of keeping a copy. With VITE_API_URL unset the base stays
   relative and MSW keeps intercepting (the demo); set to the Worker's origin
   it is the real transport, cookies included (the bmr_session cross-origin
   rule, contract 00 §0.3). Error bodies are the API's { error, message }:
   screens read `code` to branch and `message` is already French. */
const API_BASE = `${import.meta.env.VITE_API_URL ?? ''}/v1`
/** The build names an API (staging, production): the writes wired so far go to it, and the supports are read from it.
    The demo (no VITE_API_URL) stays in this browser. */
export const apiWrites = !!import.meta.env.VITE_API_URL

/** One point a check refuses (contract 05 §5.3): codes, never sentences (C-2) — the app writes the sentence from
    `code` + `field`. A `plan_gate` issue also names `what`. */
export interface ApiIssue { step: string; code: string; field: string | null; what?: string }
export class ApiError extends Error {
  constructor(public status: number, public code: string | null, message: string,
    /** the field the error names (`rev`, `document.shape`, …), when it names one */
    public field: string | null = null,
    /** a refused publish (422) lists every blocking point here; the first one is also `code` / `field` */
    public issues: ApiIssue[] = []) { super(message) }
}

/** The API's error body → ApiError (contract 00 · errors: { error: { code, message, field } }, plus `issues` on a
    refused publish; routes not yet moved answer { error: 'code' }). */
async function fail(res: Response, path: string): Promise<never> {
  const e = await res.json().catch(() => null) as { error?: string | { code?: string; message?: string; field?: string | null }; message?: string; issues?: unknown } | null
  const err = typeof e?.error === 'object' && e.error ? e.error : { code: e?.error, message: e?.message, field: null }
  const issues = Array.isArray(e?.issues) ? (e!.issues as ApiIssue[]).filter(i => i && typeof i.code === 'string') : []
  throw new ApiError(res.status, err.code ?? null, err.message ?? `${res.status} on ${path}`, err.field ?? null, issues)
}

export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  /* the API takes a write only as JSON (apps/api lib/merchant.js › checkWrite: 415 json_required otherwise), with a body or
     none (« Mettre en pause », « Annuler les modifications ») */
  const write = method !== 'GET' && method !== 'HEAD'
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    ...(write ? { headers: { 'content-type': 'application/json' } } : {}),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!res.ok) await fail(res, path)
  return (res.status === 204 ? undefined : await res.json()) as T
}

/** An uploaded image (contract 02 · POST /v1/app/media): `url` goes into brand.logoUrl or look.photo. */
export interface Media { id: string; url: string; type: string; bytes: number }

/** Sends the image itself as the body (JPEG, PNG or WebP, 5 Mo au plus); 413 / 415 come back as ApiError
    with the API's French message. Owner and admin only. */
export async function uploadMedia(file: File): Promise<Media> {
  const res = await fetch(`${API_BASE}/app/media`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': file.type || 'application/octet-stream' }, body: file,
  })
  if (!res.ok) await fail(res, '/app/media')
  return await res.json() as Media
}

export const get = <T>(path: string): Promise<T> => call<T>('GET', path)

/* ---------- hooks — the only import screens use ---------- */
export const useOverview = () => useQuery({ queryKey: ['overview'], queryFn: () => get<Overview>('/overview') })
/** The demo's fixture campaigns. With an API every campaign is the server's, read in full at the app's root
    (lib/server · lib/campaign getCampaigns): this list is then empty, and no screen reads a fixture. */
export const useCampaigns = () => useQuery({ queryKey: ['campaigns'], queryFn: () => apiWrites ? Promise.resolve([] as Campaign[]) : get<Campaign[]>('/campaigns') })
/** The gifts' figures and types. With an API, GET /v1/app/gifts/summary (contract 03 §3.3), in the app's words. */
export const useGiftSummary = () => useQuery({ queryKey: ['gifts'], queryFn: () => apiWrites
  ? get<AppGiftSummary>('/app/gifts/summary').then(giftSummaryOfApp)
  : get<GiftSummary>('/gifts/summary') })
/** Pages of 50 by last visit; `location` narrows to one establishment (D-31), as the API's ?location= does. */
export const useClients = (location?: string) => useInfiniteQuery({
  queryKey: ['clients', location ?? 'all'],
  initialPageParam: '',
  queryFn: ({ pageParam }) => {
    const q = new URLSearchParams()
    if (location) q.set('location', location)
    if (pageParam) q.set('cursor', pageParam)
    return get<ClientsCohorts>(`/app/clients${q.size ? `?${q}` : ''}`)
  },
  getNextPageParam: last => last.nextCursor ?? undefined,
})
/** The supports. With an API, GET /v1/app/supports (contract 06 §6.2) is the whole store: its pointers and figures;
    the demo reads its fixtures, which mergeSupports completes from this browser. */
export const useSupports = () => useQuery({ queryKey: ['supports'], queryFn: () => apiWrites
  ? get<AppSupport[]>('/app/supports').then(rows => rows.map(supportOfApp))
  : get<Support[]>('/supports') })
/** The establishments. With an API, GET /v1/app/locations (contract 02 §2.1): the server's ids, and its fields. */
export const useLocations = () => useQuery({ queryKey: ['locations'], queryFn: () => apiWrites ? get<Location[]>('/app/locations') : get<Location[]>('/locations') })
export const useInsights = () => useQuery({ queryKey: ['insights'], queryFn: () => get<Insight[]>('/results/insights') })
export const useEvents = () => useQuery({ queryKey: ['events'], queryFn: () => get<EventRow[]>('/events') })

/* ---------- MA-8 · the test (Technical's test mode: contract 05 §5.7b, 06, FQ-I16) ----------
   The only read that shows a test: the latest test link opened on one of the campaign's supports, the latest test
   participation with its gift, and that gift validated at a till in mode test (`by` = who did it; null again after
   an undo). A test counts nowhere else. A screen polls it while it is open: no tick is ever set by a click. */
export interface CampaignTest {
  scan: { at: string } | null
  play: { at: string; gift: { code: string; label: string; status: string; expiresAt: string } | null } | null
  validated: { at: string; by: string | null } | null
  /** per-visit ruling P6 (05 §5.7b, rule 8) · on a live Parcours, one row per position: its latest test play and validation,
      the version it was played on, and `stale` when the visit's content (gift, order, ask) changed since. Absent on a Simple. */
  visits?: { position: number; play: CampaignTest['play']; validated: CampaignTest['validated']; version: number | null; stale: boolean }[]
  /** lib-pack-22 · 0037 (05 §5.7b, P5; absent when there is no draft) · the tests of the content being written, one row per visit
      1…len of the draft, read from its test version of `rev` only: `tested` = the visit passes § 5.5 rule 11 (played and
      identified; a gift visit also validated at a till in mode test). One source for « N / M visites testées pour ce contenu »
      and the publish gate (422 visits_untested). */
  pending?: { rev: string; visits: { position: number; play: CampaignTest['play']; validated: CampaignTest['validated']; tested: boolean }[] }
}
/** lib-pack-22 · the draft's count: « N / M visites testées » (null without a draft). */
export const draftTests = (t: CampaignTest | undefined): { done: number; total: number } | null =>
  t?.pending ? { done: t.pending.visits.filter(v => v.tested).length, total: t.pending.visits.length } : null
/** lib-pack-22 · the first visit of the draft still to test (1-based), null when every one is tested or there is no draft:
    the app-side gate on « Publier » (wireframes ruling 20/10 § 2; the API refuses 422 visits_untested the same way). */
export const untestedVisit = (t: CampaignTest | undefined): number | null => t?.pending?.visits.find(v => !v.tested)?.position ?? null
export const useCampaignTest = (id: string | undefined, poll = false) => useQuery({
  queryKey: ['campaign-test', id], enabled: !!id,
  queryFn: () => get<CampaignTest>(`/app/campaigns/${encodeURIComponent(id!)}/test`),
  refetchInterval: poll ? 5000 : false,
})
/** The test loop is closed: the gift validated in mode test, or, on a campaign with no gift, the parcours played. */
export const testDone = (t: CampaignTest | undefined, noGift: boolean): boolean => !!t && (noGift ? !!t.play : !!t.validated)
/** GET /v1/app/supports/:id/test-link: a signed link to what the support opens, played as a test, for two hours.
    A fresh one on every call: ask it each time it is shown, and never keep it. */
export interface TestLink { url: string; expiresAt: string }
/** P6 · `visit` (1-based) asks for a link that plays that visit's experience and ask, in test mode only (06 `?visit=N`). */
export const getTestLink = (supportId: string, visit?: number) => get<TestLink>(`/app/supports/${encodeURIComponent(supportId)}/test-link${visit ? `?visit=${visit}` : ''}`)
/** lib-pack-22 · 0037 (05 §5.3, A1) · « Tester cette visite » on the content being written — no support needed. The link runs the
    real journey on the draft's test version (`url` = go…/d/{testVersionId}?test=t1.…&visit=N, two hours), at the draft's first
    establishment, in test mode only. 409 not_frozen until createTestVersion ran for this rev; 409 no_pending without a draft;
    422 invalid_field `visit` outside 1…the draft's visits. A fresh one each time it is shown, never kept. */
export interface DraftTestLink { url: string; expiresAt: string; testVersionId: string; rev: string }
export const getDraftTestLink = (campaignId: string, visit = 1) =>
  get<DraftTestLink>(`/app/campaigns/${encodeURIComponent(campaignId)}/test-link?draft=1&visit=${visit}`)

/* ---------- notifications (nine-answers item 7 · Technical's contract answer 08/10) ----------
   One truth for the bell, the rows and the digest: the typed `notifications` ledger, read through
   GET /v1/app/notifications?since= (7 days by default, 30 at most). Five kinds, named per server
   trigger; none of them names a review. Clocks are Europe/Paris on this side (the merchant's clock);
   the plan-quota month stays UTC on the server (T1.9) — a line never says « ce mois (UTC) », it names
   the date the fact happened. */
export type NotificationKind = 'quota_paused' | 'slice_capped' | 'feedback_received' | 'campaign_ended' | 'prize_cancelled'
/** What the feed carries: the five mail kinds above, plus `quota_warning` (11.1: the 80 % and 100 % facts,
    never mailed, so it has no preference row). */
export type FeedKind = NotificationKind | 'quota_warning'
/** One fact, as contract 11 § 11.3 sends it: the ids are null when they do not apply, `meta` carries the
    kind's own facts (slice_capped → { label, cap, roueId }), `sentAt` is the first mail or digest that
    carried it. The server sends one row per fact; it never groups. */
export interface NotificationItem {
  id: string; kind: FeedKind; at: string
  label?: string
  locationId?: string | null; campaignId?: string | null; giftTypeId?: string | null
  prizeCode?: string | null; feedbackId?: string | null
  meta?: Record<string, unknown>
  sentAt?: string | null
  readAt: string | null
  /** NOT on the wire. mocks/data-b still groups a day's retours with it; it goes when that fixture
      sends one row per fact. No screen may read it. */
  count?: number
}
export interface NotificationsFeed { items: NotificationItem[]; unread: number }
/** The feed of the last `days` days (7 by default, 30 at most), newest first. The key moves once an hour so a
    screen left open does not refetch on every render. */
export const useNotifications = (days = 7) => {
  const d = Math.min(30, Math.max(1, days))
  const since = new Date(Math.floor(Date.now() / 3600e3) * 3600e3 - d * 86400e3).toISOString()
  return useQuery({ queryKey: ['notifications', since], queryFn: () => get<NotificationsFeed>(`/app/notifications?since=${encodeURIComponent(since)}`) })
}
/** POST /v1/app/notifications/read — the facts the centre has shown are read; per workspace in V1 (11.3). */
export const markNotificationsRead = (ids: string[]) => call<void>('POST', '/app/notifications/read', { ids })

/* Préférences — ONE path, GET / PUT /v1/app/messages/preferences, stored per user. E-mail is the only channel
   in V1; `locked` rows (N1 quota en pause) stay `immediate` whatever is sent — the API answers 422 with the
   reason the screen shows. The digest hour and zone ride along so no screen hard-codes « 08:00 ». */
export type NotificationCadence = 'immediate' | 'daily' | 'off'
export interface NotificationPrefRow { event: NotificationKind; channel: 'email'; cadence: NotificationCadence; locked: boolean }
export interface NotificationPreferences { rows: NotificationPrefRow[]; digestHour: number; timezone: string }
export const useNotificationPreferences = () =>
  useQuery({ queryKey: ['notifications', 'preferences'], queryFn: () => get<NotificationPreferences>('/app/messages/preferences') })
export const saveNotificationPreferences = (rows: { event: NotificationKind; cadence: NotificationCadence }[]) =>
  call<NotificationPreferences>('PUT', '/app/messages/preferences', { rows })
